// src/components/Lessons.js
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  IconButton,
  Container,
  Card,
  CardContent,
  Fade,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Add,
  Delete,
  Logout,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ArrowBack,
  Edit,
  PlayCircle,
  OndemandVideo,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { ref, set, get, child, remove } from 'firebase/database';
import { auth, database } from '../firebase/Config';
import { useNavigate, useParams } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  p: 4,
};

const Lessons = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [lessonData, setLessonData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    order: 1,
    duration: '',
  });
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    lessonId: null,
    lessonTitle: '',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Extrair ID do vídeo do YouTube de várias URLs
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!lessonData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }

    if (!lessonData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (!lessonData.videoUrl.trim()) {
      newErrors.videoUrl = 'URL do vídeo é obrigatória';
    } else if (!getYouTubeVideoId(lessonData.videoUrl)) {
      newErrors.videoUrl = 'URL do YouTube inválida';
    }

    if (!lessonData.order || lessonData.order < 1) {
      newErrors.order = 'Ordem deve ser maior que 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpen = () => {
    setErrors({});
    setIsEditing(false);
    setLessonData({
      title: '',
      description: '',
      videoUrl: '',
      order: lessons.length + 1,
      duration: '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditing(false);
    setErrors({});
    setLessonData({
      title: '',
      description: '',
      videoUrl: '',
      order: 1,
      duration: '',
    });
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const fetchCourse = async () => {
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, `courses/${courseId}`));
      if (snapshot.exists()) {
        setCourse({ id: courseId, ...snapshot.val() });
      } else {
        showSnackbar('Curso não encontrado', 'error');
        navigate('/admin/courses');
      }
    } catch (error) {
      console.error('Erro ao buscar curso: ', error);
      showSnackbar('Erro ao carregar curso', 'error');
    }
  };

  const fetchLessons = async () => {
    setLoading(true);
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'lessons'));
      if (snapshot.exists()) {
        const lessonsData = snapshot.val();
        const lessonsArray = Object.keys(lessonsData)
          .map((key) => ({
            id: key,
            ...lessonsData[key],
          }))
          .filter(lesson => lesson.courseId === courseId)
          .sort((a, b) => a.order - b.order);
        setLessons(lessonsArray);
      } else {
        setLessons([]);
      }
    } catch (error) {
      console.error('Erro ao buscar aulas: ', error);
      showSnackbar('Erro ao carregar aulas', 'error');
    }
    setLoading(false);
  };

  const handleAddLesson = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const lessonId = isEditing ? lessonData.id : `lesson_${new Date().getTime()}`;
      const videoId = getYouTubeVideoId(lessonData.videoUrl);

      await set(ref(database, `lessons/${lessonId}`), {
        courseId: courseId,
        title: lessonData.title,
        description: lessonData.description,
        videoUrl: lessonData.videoUrl,
        videoId: videoId,
        order: parseInt(lessonData.order),
        duration: lessonData.duration,
        createdBy: isEditing ? lessonData.createdBy : userEmail,
        createdAt: isEditing ? lessonData.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      showSnackbar(
        isEditing ? 'Aula atualizada com sucesso!' : 'Aula cadastrada com sucesso!',
        'success'
      );
      handleClose();
      fetchLessons();
    } catch (error) {
      console.error('Erro ao salvar aula: ', error);
      showSnackbar('Erro ao salvar aula', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (lesson) => {
    setLessonData(lesson);
    setIsEditing(true);
    setOpen(true);
  };

  const handleOpenDeleteDialog = (lesson) => {
    setDeleteDialog({
      open: true,
      lessonId: lesson.id,
      lessonTitle: lesson.title,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, lessonId: null, lessonTitle: '' });
  };

  const handleDeleteLesson = async () => {
    try {
      await remove(ref(database, `lessons/${deleteDialog.lessonId}`));
      showSnackbar('Aula excluída com sucesso!', 'success');
      fetchLessons();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir aula: ', error);
      showSnackbar('Erro ao excluir aula', 'error');
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    fetchCourse();
    fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const columns = [
    {
      field: 'order',
      headerName: 'Ordem',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor: '#48bb78',
            color: '#fff',
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      field: 'title',
      headerName: 'Título da Aula',
      width: 350,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlayCircle sx={{ color: '#e53935', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'duration',
      headerName: 'Duração',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'videoId',
      headerName: 'Preview',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params.value) return '-';
        const thumbnailUrl = `https://img.youtube.com/vi/${params.value}/default.jpg`;
        return (
          <img
            src={thumbnailUrl}
            alt="Thumbnail"
            style={{
              width: '120px',
              height: '90px',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Data de Criação',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return date.toLocaleDateString('pt-BR');
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <IconButton
            onClick={() => handleEdit(params.row)}
            sx={{ color: '#48bb78', '&:hover': { bgcolor: '#48bb7820' } }}
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => handleOpenDeleteDialog(params.row)}
            sx={{ color: '#e53935', '&:hover': { bgcolor: '#e5393520' } }}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/admin/courses')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <OndemandVideo sx={{ mr: 2, fontSize: 32 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: '0.5px',
              }}
            >
              Gerenciar Aulas
            </Typography>
            {course && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Curso: {course.title}
              </Typography>
            )}
          </Box>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#48bb78' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2d3748',
                      mb: 1,
                    }}
                  >
                    <PlayCircle sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32, color: '#48bb78' }} />
                    Aulas do Curso
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Total de aulas: <strong>{lessons.length}</strong>
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleOpen}
                  sx={{
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Nova Aula
                </Button>
              </Box>

              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={lessons}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  checkboxSelection
                  disableSelectionOnClick
                  loading={loading}
                  rowHeight={100}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#f7fafc',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f7fafc',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Modal de Nova/Editar Aula */}
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  {isEditing ? 'Editar Aula' : 'Adicionar Nova Aula'}
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              <TextField
                label="Título da Aula"
                fullWidth
                margin="normal"
                required
                value={lessonData.title}
                onChange={(e) => {
                  setLessonData({ ...lessonData, title: e.target.value });
                  if (errors.title) setErrors({ ...errors, title: null });
                }}
                error={!!errors.title}
                helperText={errors.title}
              />

              <TextField
                label="Descrição"
                fullWidth
                margin="normal"
                required
                multiline
                rows={3}
                value={lessonData.description}
                onChange={(e) => {
                  setLessonData({ ...lessonData, description: e.target.value });
                  if (errors.description) setErrors({ ...errors, description: null });
                }}
                error={!!errors.description}
                helperText={errors.description}
              />

              <TextField
                label="URL do Vídeo do YouTube"
                fullWidth
                margin="normal"
                required
                value={lessonData.videoUrl}
                onChange={(e) => {
                  setLessonData({ ...lessonData, videoUrl: e.target.value });
                  if (errors.videoUrl) setErrors({ ...errors, videoUrl: null });
                }}
                error={!!errors.videoUrl}
                helperText={errors.videoUrl || 'Cole o link completo do vídeo do YouTube'}
                placeholder="https://www.youtube.com/watch?v=..."
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Ordem"
                  type="number"
                  margin="normal"
                  required
                  value={lessonData.order}
                  onChange={(e) => {
                    setLessonData({ ...lessonData, order: e.target.value });
                    if (errors.order) setErrors({ ...errors, order: null });
                  }}
                  error={!!errors.order}
                  helperText={errors.order || 'Posição da aula no curso'}
                  sx={{ flex: 1 }}
                />

                <TextField
                  label="Duração (opcional)"
                  margin="normal"
                  value={lessonData.duration}
                  onChange={(e) => setLessonData({ ...lessonData, duration: e.target.value })}
                  placeholder="Ex: 15 min"
                  helperText="Exemplo: 15 min, 1h 30min"
                  sx={{ flex: 1 }}
                />
              </Box>

              {lessonData.videoUrl && getYouTubeVideoId(lessonData.videoUrl) && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: '#718096' }}>
                    Preview do vídeo:
                  </Typography>
                  <Box
                    component="img"
                    src={`https://img.youtube.com/vi/${getYouTubeVideoId(lessonData.videoUrl)}/hqdefault.jpg`}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      maxWidth: 400,
                      borderRadius: 2,
                      border: '2px solid #e2e8f0',
                    }}
                  />
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    py: 1.5,
                    borderColor: '#718096',
                    color: '#718096',
                    '&:hover': {
                      borderColor: '#2d3748',
                      bgcolor: '#f7fafc',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddLesson}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.4)',
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <>
                      <CheckCircle sx={{ mr: 1 }} />
                      {isEditing ? 'Atualizar' : 'Cadastrar'}
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#e53935' }} />
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir a aula <strong>{deleteDialog.lessonTitle}</strong>?
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              sx={{ color: '#718096' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteLesson}
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                '&:hover': { bgcolor: '#c62828' }
              }}
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Lessons;
